import React, { useEffect, useState } from "react";
import { Button, Row, Col, Modal } from "antd";
import { EyeOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import Image from "./Image";
import { Image as AntImage } from "antd";
import styled from "styled-components";

interface ArrayImageProps {
  value?: string[] | { url: string }[];
  onChange?: (value: string[]) => void;
  onAddClick?: () => void;
  schema?: any;
  disabled?: boolean;
}

const ArrayImage: React.FC<ArrayImageProps> = ({
  value,
  onChange,
  schema,
  disabled,
}) => {
  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    if (value && Array.isArray(value)) {
      const urls = value.map((item) =>
        typeof item === "string" ? item : item.url,
      );
      setImages(urls);
    } else {
      setImages([]);
    }
  }, [value]);

  const handleImageChange = (index: number) => (newValue: any) => {
    const newImages = [...images];
    newImages[index] =
      typeof newValue === "string" ? newValue : newValue.file.response;
    setImages(newImages);
    onChange?.(newImages);
  };

  const handleAddImages = (newValue: string | string[]) => {
    if (disabled) return;
    const newUrls = Array.isArray(newValue) ? newValue : [newValue];

    setImages((prevImages) => {
      const nextImages = [...prevImages];
      let hasChange = false;

      newUrls.filter(Boolean).forEach((url) => {
        if (!nextImages.includes(url) && nextImages.length < 5) {
          nextImages.push(url);
          hasChange = true;
        }
      });

      if (hasChange) {
        onChange?.(nextImages);
        return nextImages;
      }
      return prevImages;
    });
  };

  const handleRemoveImage = (index: number) => {
    Modal.confirm({
      title: "Xoá hình ảnh",
      content: "Bạn có chắc chắn muốn xóa hình ảnh này?",
      okText: "Đồng ý",
      onOk: () => {
        const newImages = images.filter((_, i) => i !== index);
        setImages(newImages);
        onChange?.(newImages);
      },
    });
  };

  return (
    <AntImage.PreviewGroup>
      <ImageGrid gutter={[8, 8]}>
        {images.map((image, index) => (
          <Col key={index} xs={12} sm={8} md={6} lg={4}>
            <ImageContainer>
              {image ? (
                <StyledAntImage
                  src={image}
                  alt={`Image ${index + 1}`}
                  preview={{
                    mask: <EyeOutlined style={{ fontSize: "18px" }} />,
                  }}
                />
              ) : (
                <Image
                  value={image}
                  onChange={handleImageChange(index)}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  disabled={disabled}
                />
              )}
              {!disabled && (
                <DeleteButton
                  icon={<DeleteOutlined style={{ fontSize: "10px" }} />}
                  onClick={() => handleRemoveImage(index)}
                />
              )}
            </ImageContainer>
          </Col>
        ))}
        {!disabled && images.length < 5 && (
          <Col xs={12} sm={8} md={6} lg={4}>
            <AddUploadContainer>
              <Image
                multiple
                maxCount={5}
                value={[]}
                onChange={handleAddImages}
                title="Add"
                tooltip="Add"
                style={{
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.5 : 1,
                  pointerEvents: disabled ? "none" : "auto",
                }}
                disabled={disabled}
              >
                <AddUploadContent>
                  <PlusOutlined style={{ fontSize: 20, color: "#1890ff" }} />
                  <span>Add</span>
                </AddUploadContent>
              </Image>
            </AddUploadContainer>
          </Col>
        )}
      </ImageGrid>
    </AntImage.PreviewGroup>
  );
};

const ImageGrid = styled(Row)`
  margin-top: 8px;
`;

const ImageContainer = styled.div`
  position: relative;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
  aspect-ratio: 1 / 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const StyledAntImage = styled(AntImage)`
  .ant-image-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const DeleteButton = styled(Button)`
  position: absolute;
  top: 2px;
  right: 2px;
  background: rgba(255, 255, 255, 0.7);
  border: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  padding: 0;
  font-size: 10px;

  .anticon {
    font-size: 10px;
  }

  &:hover {
    background: rgba(255, 59, 48, 0.8);
    color: white;
  }
`;

const AddUploadContainer = styled.div`
  aspect-ratio: 1 / 1;
  width: 100%;

  .ant-upload.ant-upload-select-picture-card {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1 / 1;
    margin: 0;
    border-color: #1890ff;
    background-color: #f0f8ff;
    border-style: dashed;
  }

  .ant-upload.ant-upload-select-picture-card > .ant-upload {
    width: 100%;
  }
`;

const AddUploadContent = styled.div`
  color: #1890ff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
  aspect-ratio: 1 / 1;
  width: 100%;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background-color: #f5f5f5;
  }
  &:hover {
    border-color: #1890ff;
    background-color: #f0f8ff;
  }

  span {
    margin-top: 4px;
    color: #1890ff;
    font-size: 13px;
    font-weight: 500;
    width: 100%;
  }
`;

export default ArrayImage;
